import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getWeekDays,
  getCurrentWeekData,
  getCurrentWeekDataFromMonthYear,
  transformDateTimeZone,
  getWeekInfo
} from "../services/dateTimeService";
import UserRegisterContext from "./user-register-context";
import { logOut } from "../../features/userSlice";
import { getAllocatedShifts } from "../services/allocatedShifts";
import { getScheduleTimes } from "../services/userRegisterService";
import { setLoading } from "../../features/loadingSlice";
import { restartPermissions } from "../../features/resourcePepermissionsSlice";
import {
  updatecurrentWeekId,
  setSelectedLocationState,
  selectedLocationState,
  selectedMonthState,
  selectedYearState,
  setSelectedMonthState,
  setSelectedYearState,
  setloadedLocations
} from "../../features/currentDataSlice";
import { addUserToCosmosDB } from "../../features/cosmosdbSlice";
import { API_BASE_URL, HEADER_SUBSCRIPTION_KEY, HOME_ID } from "../services/global";
import { getNextSaturday, getPreviousSunday, shortNextSaturday, shortPreviousSunday } from "../../utils/utils";
import { getToken } from "../services/userRegisterService";

const useLogout = () => {
  const dispatch = useDispatch();
  const handleLogout = () => {
    dispatch(logOut());
    dispatch(restartPermissions());
  };
  return handleLogout;
};

const UserRegisterProvider = (props) => {
  const [selectedWeek, setSelectedWeek] = useState({});
  const [usersRegisters, setUsersRegisters] = useState([]);
  const [allocatedShiftsData, setAllocatedShiftsData] = useState({});
  const [scheduleTimes, setScheduleTimes] = useState([]);
  const [transferUsers, setTransferUsers] = useState([]);
  const handleLogout = useLogout();
  const dispatch = useDispatch();

  //CURRENT DATA FROM SLICE
  const selectedLocationSlice = useSelector(selectedLocationState);
  const selectedMonthSlice = useSelector(selectedMonthState);
  const selectedYearSlice = useSelector(selectedYearState);
  const refreshTimeoutRef = useRef(null);
  const isRealtimeConnectedRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const [isSseStable, setIsSseStable] = useState(false);
  const isSseEnabled = useMemo(
    () => process.env.REACT_APP_ENABLE_SHIFT_SSE !== "false",
    []
  );

  async function fetchAllocatedShiftsData(location, startDate, endDate) {
    const data = await getAllocatedShifts(location.id, startDate, endDate);
    if (data) {
      setAllocatedShiftsData(data);
    }
  }

  async function fetchScheduleTimes() {
    const data = await getScheduleTimes();
    if (data) {
      setScheduleTimes(data.load);
    }
  }

  const fetchCurrentWeekData = useCallback(
    async (location, startDate, endDate, year, month, pageId) => {
      try {
        if (location.id && startDate && endDate) {
          dispatch(setLoading(true));
          fetchAllocatedShiftsData(location, startDate, endDate);
          localStorage.setItem('lastViewed', startDate);
          localStorage.setItem('lastLocation', location.id);
          let data = await getCurrentWeekData(
            location.id,
            startDate,
            endDate
          );
          data = {...data, initialDate:startDate, finalDate:endDate};
          if (data) {
            if (data.initialDate && data.finalDate) {
              transformFetchDate(data, location, year.id, month.id, pageId);
            }
          }
          dispatch(setLoading(false));
        }
      } catch (err) {
        console.log(err);
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  const fetchWeekDataFromYearMonth = async (location, yearObj, monthId, pageId) => {
    try {
      const year = parseInt(yearObj.label, 10);
      if (location.id && year && monthId) {
        let [startDate, endDate] = getFirstSundayAndNextSaturday(monthId, year);
        localStorage.setItem('lastViewed', startDate);
        localStorage.setItem('lastLocation', location.id);
        dispatch(setLoading(true));
        fetchAllocatedShiftsData(location, startDate, endDate);
        const data = await getCurrentWeekData(
          parseInt(location.id),
          startDate,
          endDate
        );
        if (data) {
          if (data.initialDate && data.finalDate) {
            transformFetchDate(data, location, yearObj.id, monthId, pageId);
          }
        }
        dispatch(setLoading(false));
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  const transformFetchDate = (data, location, yearId, monthId, pageId) => {
    const formattedInitialDate = transformDateTimeZone(
      data.initialDate,
      location.timeZone
    );
    const formattedFinalDate = transformDateTimeZone(
      data.finalDate,
      location.timeZone
    );
    const { months, days, shortNameMonth } = getWeekInfo(
      formattedInitialDate,
      formattedFinalDate
    );
    const week = {
      status: data.status,
      masterId: data.masterId,
      startDate: formattedInitialDate,
      endDate: formattedFinalDate,
      month: months,
      day: days,
      week: getWeekDays(formattedInitialDate),
      shortNameMonth
    };
    setSelectedWeek(week);

    //cosmo db testing
    dispatch(updatecurrentWeekId(data.masterId));

    
      setUsersRegisters(
        data?.transfers?.length > 0 ?
        [{id:'transfer', groupName: "Users Transfers", employees: data.transfers ?? []}, ...(data?.groups ?? [])]
        :
        (data?.groups ?? [])
      );
    
    if (data?.transfers?.length > 0) {
      setTransferUsers([]);
    } else {
      setTransferUsers([]);
    }

    //send data to cosmos DB
    dispatch(
      addUserToCosmosDB({
        pageId,
        locationId: location.id,
        weekId: pageId === 0 ? data.masterId : 0,
        monthId: monthId,
        year: yearId,
      })
    );
  };

  const changeSelectedLocation = (location, isMonthView, pageId) => {
    dispatch(setSelectedLocationState(location));
    const previousSunday = getPreviousSunday();
    const nextSaturday = getNextSaturday();
    if (!isMonthView) {
      fetchCurrentWeekData(
        location,
        (selectedWeek?.startDate?.toISOString() ?? previousSunday.toISOString()).slice(0, 10),
        (selectedWeek?.endDate?.toISOString() ?? nextSaturday.toISOString()).slice(0, 10),
        selectedYearSlice,
        selectedMonthSlice,
        pageId
      );
    } else {
      //fetch service for month-view
    }
  };

  const changeSelectedYear = (year, isMonthView, pageId) => {
    dispatch(setSelectedYearState(year));
    if (!isMonthView) {
      fetchWeekDataFromYearMonth(
        selectedLocationSlice,
        year,
        selectedMonthSlice.id,
        pageId
      );
    } else {
      //fetch data from month view
    }
  };

  const resetData = async () => {
    dispatch(setSelectedLocationState({}));
    dispatch(setloadedLocations([]));
    setAllocatedShiftsData([]);
    setTransferUsers([]);
    setScheduleTimes([]);
    setUsersRegisters([]);
  }


  const changeSelectedMonth = (month, isMonthView, pageId) => {
    dispatch(setSelectedMonthState(month));
    if (!isMonthView) {
      fetchWeekDataFromYearMonth(
        selectedLocationSlice,
        selectedYearSlice,
        month.id,
        pageId
      );
    } else {
      //fetch data for month view
    }
  };

  const changeSelectedWeek = (date) => {
    const stringStartDate = date.startDate.toISOString().slice(0, 10);
    const stringEndDate = date.endDate.toISOString().slice(0, 10);
    fetchCurrentWeekData(
      selectedLocationSlice,
      stringStartDate,
      stringEndDate,
      selectedYearSlice,
      selectedMonthSlice,
      HOME_ID
    );
    const week = {
      masterId: date.masterId,
      status: date.status,
      startDate: date.startDate,
      endDate: date.endDate,
      month: date.month,
      day: date.day,
      week: getWeekDays(date.startDate),
    };
    setSelectedWeek(week);
  };

  const refreshVisibleWeek = useCallback(() => {
    const locationId = selectedLocationSlice?.id;
    const weekStart = selectedWeek?.startDate?.toISOString?.().slice(0, 10);
    const weekEnd = selectedWeek?.endDate?.toISOString?.().slice(0, 10);

    if (!locationId || !weekStart || !weekEnd || isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;
    Promise.resolve(
      fetchCurrentWeekData(
        selectedLocationSlice,
        weekStart,
        weekEnd,
        selectedYearSlice,
        selectedMonthSlice,
        HOME_ID
      )
    ).finally(() => {
      isRefreshingRef.current = false;
    });
  }, [
    selectedLocationSlice,
    selectedWeek?.startDate,
    selectedWeek?.endDate,
    selectedYearSlice,
    selectedMonthSlice,
    fetchCurrentWeekData,
  ]);

  const updateShiftTransmittedStatus = useCallback((payload) => {
    const shiftId = Number(payload?.shiftId ?? payload?.id);
    const transmittedStatus = payload?.transmittedStatus;

    if (Number.isNaN(shiftId) || transmittedStatus === undefined || transmittedStatus === null) {
      return;
    }

    setUsersRegisters((prevGroups) =>
      prevGroups.map((group) => ({
        ...group,
        employees: (group.employees ?? []).map((groupEmployee) => {
          const employeeShifts = groupEmployee?.employee?.shifts;
          if (!Array.isArray(employeeShifts)) {
            return groupEmployee;
          }

          const updatedShifts = employeeShifts.map((shift) => {
            if (Number(shift?.id) !== shiftId) {
              return shift;
            }
            return { ...shift, transmittedStatus };
          });

          return {
            ...groupEmployee,
            employee: {
              ...groupEmployee.employee,
              shifts: updatedShifts,
            },
          };
        }),
      }))
    );
  }, []);

  useEffect(() => {
    // SSE is enabled by default; set REACT_APP_ENABLE_SHIFT_SSE=false to disable it.
    if (!isSseEnabled) {
      isRealtimeConnectedRef.current = false;
      setIsSseStable(false);
      return;
    }

    const locationId = selectedLocationSlice?.id;
    const weekStart = selectedWeek?.startDate?.toISOString?.().slice(0, 10);
    const weekEnd = selectedWeek?.endDate?.toISOString?.().slice(0, 10);

    if (!locationId || !weekStart || !weekEnd) {
      return;
    }

    const token = getToken();
    if (!token) {
      isRealtimeConnectedRef.current = false;
      setIsSseStable(false);
      return;
    }

    const params = new URLSearchParams({
      locationId: String(locationId),
      weekStart,
      weekEnd,
    });
    let abortController = null;
    let reconnectTimeoutId = null;
    let cancelled = false;

    const handleEvent = (eventName, eventData) => {
      const allowedEvents = ["shift-updated", "shift-created", "shift-deleted"];

      if (!eventData) {
        return;
      }

      let payload = null;
      try {
        payload = JSON.parse(eventData);
      } catch (_error) {
        // Ignore non-JSON payloads such as heartbeat keep-alives.
        return;
      }

      const payloadType = (payload?.type || "").toLowerCase();
      const normalizedEventName = (eventName || "").toLowerCase();
      const isShiftChangeEvent = allowedEvents.includes(normalizedEventName) || allowedEvents.includes(payloadType);
      if (!isShiftChangeEvent) {
        return;
      }

      const eventLocationId = Number(payload?.locationId);
      const currentLocationId = Number(locationId);
      if (!Number.isNaN(eventLocationId) && eventLocationId !== currentLocationId) {
        return;
      }

      updateShiftTransmittedStatus(payload);
    };

    const processSseChunk = (chunk, streamState) => {
      streamState.buffer += chunk;
      const events = streamState.buffer.split("\n\n");
      streamState.buffer = events.pop() ?? "";

      events.forEach((rawEvent) => {
        let eventName = "";
        const dataParts = [];

        rawEvent.split("\n").forEach((line) => {
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          }
          if (line.startsWith("data:")) {
            dataParts.push(line.slice(5).trim());
          }
        });

        handleEvent(eventName, dataParts.join("\n"));
      });
    };

    const connect = async () => {
      abortController = new AbortController();

      try {
        const response = await fetch(
          `${API_BASE_URL}/shifts/stream?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Accept: "text/event-stream",
              Authorization: `Bearer ${token}`,
              "Ocp-Apim-Subscription-Key": HEADER_SUBSCRIPTION_KEY,
            },
            signal: abortController.signal,
          }
        );

        if (!response.ok || !response.body) {
          throw new Error(`SSE response not available: ${response.status}`);
        }

        isRealtimeConnectedRef.current = true;
        setIsSseStable(true);

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        const streamState = { buffer: "" };

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) {
            throw new Error("SSE stream ended");
          }

          processSseChunk(decoder.decode(value, { stream: true }), streamState);
        }
      } catch (_error) {
        if (cancelled) {
          return;
        }

        isRealtimeConnectedRef.current = false;
        setIsSseStable(false);
        reconnectTimeoutId = setTimeout(() => {
          connect();
        }, 3000);
      }
    };

    connect();

    return () => {
      cancelled = true;
      isRealtimeConnectedRef.current = false;
      setIsSseStable(false);
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (abortController) {
        abortController.abort();
      }
    };
  }, [
    selectedLocationSlice,
    selectedWeek?.startDate,
    selectedWeek?.endDate,
    selectedYearSlice,
    selectedMonthSlice,
    fetchCurrentWeekData,
    refreshVisibleWeek,
    updateShiftTransmittedStatus,
    isSseEnabled,
  ]);

  useEffect(() => {
    if (isSseEnabled && isSseStable) {
      return;
    }

    const intervalId = setInterval(() => {
      if (document.visibilityState !== "visible") {
        return;
      }
      if (isRealtimeConnectedRef.current) {
        return;
      }
      refreshVisibleWeek();
    }, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [refreshVisibleWeek, isSseEnabled, isSseStable]);

  const userRegisterContext = {
    selectedWeek: {
      status : selectedWeek.status,
      masterId: selectedWeek.masterId,
      startDate: selectedWeek.startDate,
      endDate: selectedWeek.endDate,
      monthString: selectedWeek.month,
      dayString: selectedWeek.day,
      week: selectedWeek.week,
      shortNameMonth: selectedWeek.shortNameMonth
    },
    allocatedShiftsData: allocatedShiftsData,
    scheduleTimes: scheduleTimes,
    currentWeekData: usersRegisters,
    transferUsers: transferUsers,
    changeWeek: changeSelectedWeek,
    changeMonth: changeSelectedMonth,
    changeLocation: changeSelectedLocation,
    changeYear: changeSelectedYear,
    fetchWeekData: fetchCurrentWeekData,
    resetData: resetData,
    fetchWeekDataFromYearMonth: fetchWeekDataFromYearMonth,
    fetchScheduleTimes: fetchScheduleTimes,
  };

  return (
    <UserRegisterContext.Provider value={userRegisterContext}>
      {props.children}
    </UserRegisterContext.Provider>
  );
};

export default UserRegisterProvider;

function getFirstSundayAndNextSaturday(month, year) {
  // Create a new date object with the given month and year
  const dateString = new Date(`${year}-${month.toString().padStart(2, 0)}-01`).toISOString().slice(0,10);
  return [shortPreviousSunday(dateString), shortNextSaturday(dateString)];

  // Find the first Sunday in the month
  // while (date.getDay() !== 0) {
  //   date.setDate(date.getDate() + 1);
  // }
  // const firstSunday = date.getDate();

  // // Find the following Saturday
  // date.setDate(date.getDate() + 6);
  // const nextSaturday = date.getDate();

  // // Format the dates as strings in the format (YYYY-MM-DD)
  // const firstSundayString = `${date.getFullYear()}-${(date.getMonth() + 1)
  //   .toString()
  //   .padStart(2, "0")}-${firstSunday.toString().padStart(2, "0")}`;

  // const nextSaturdayString = `${date.getFullYear()}-${(date.getMonth() + 1)
  //   .toString()
  //   .padStart(2, "0")}-${nextSaturday.toString().padStart(2, "0")}`;

  // Return the dates as strings
  // return [firstSundayString, nextSaturdayString];
}